import React, { useState, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Import Shared Components
import { Input, Checkbox } from 'shared-components';
import { ThemeContext } from 'styled-components';
// Import Shared Hooks
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
import { verifyCardValidations } from 'shared-components/src/validations/cardValidations';
// Import Global Layouts
import InputGroup from '../../../layouts/InputGroup';
// Import Local Layouts
import AuthLayout from '../layouts/Auth';
import { AuthForm } from '../layouts/InnerAuth';
// Import Local Components
import ActivationMessage from '../components/ActivationMessage';
// Improt Component Styles
import { FieldError, FormRow, PasswordFieldWrapper } from '../styles';

import {
  validateCardNumber,
  validateCVV,
  validateExpiry,
} from '../../../utils/validationHandlers';

import { authEn } from '../../../i18n/auth';
import CardholderAgreementLink  from './components/CardholderAgreementLink';
import PrivacyPolicyAgreementLink  from './components/PrivacyPolicyAgreementLink';
import ElectronicComConsentLink  from './components/ElectronicComConsentLink';
import PwdEyeButton from './components/PwdEyeButton';

export default () => {
  const intl = useIntl();
  const page = 'activate';
  const theme = useContext(ThemeContext);

  // // Set States
  const initialCardInfoState = {
    cardNumber: '',
    cvv: '',
    expiry: '',
    consent: false,
  };
  const initialValidationErrors = {
    cardNumber: '',
    cvv: '',
    expiry: '',
    consent: false,
  };
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activationMessage, setActivationMessage] = useState(null);
  const [cvvType, setCvvType] = useState('password');

  // Create reducer with initial card information state
  // Returns the updated state to be passed to components and a function to update the state
  const [cardInfo, updateCardInfo] = useUpdateStateObject(initialCardInfoState);
  const [validationErrors, setValidationErrors] = useUpdateStateObject(
    initialValidationErrors
  );
  const [termsConsent, setTermsConsent] = useState(false);
  const [cardholderAgreement, setCardholderAgreement] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [electronicDisclosure, setElectronicDisclosure] = useState(false);

  const checkFormValidation = () => {
    const errors = validationErrors;
    // check card number
    if (!verifyCardValidations.cardNumber(cardInfo.cardNumber)) {
      errors.cardNumber = !cardInfo.cardNumber
        ? 'Card number is required'
        : 'Invalid card number, please check and try again.';
    }

    // check expiry
    if (!verifyCardValidations.expiry(cardInfo.expiry.replace(/\//g, ''))) {
      errors.expiry =
        cardInfo.expiry === ''
          ? 'Expiry is required, Format: MM/YYYY.'
          : 'Invalid expiry date, please check and try again. Format: MM/YYYY.';
    }

    // check cvv
    if (!verifyCardValidations.cvv(cardInfo.cvv)) {
      errors.cvv =
        cardInfo.cvv === ''
          ? 'CVV is required'
          : 'Invalid CVV, please check and try again.';
    }

    setValidationErrors(errors);

    // Throws a generic error message if any of the fields are not valid
    const isInValid = Object.keys(errors).find(field => {
      return errors[field];
    });

    return isInValid;
  };

  const handleApiError = error => {
    let fieldName = '';

    switch (error.code) {
    case 'invalid_card_number':
      fieldName = 'cardNumber';
      break;
    case 'card_not_activated':
      fieldName = 'cardNumber';
      break;
    case 'failed_to_verify_card':
      fieldName = 'cvv';
      break;
    case 'failed_to_verify_card_expiry_date':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_expired':
      fieldName = 'expiry';
      break;
    case 'failed_to_verify_card_cvv':
      fieldName = 'cvv';
      break;
    default:
      break;
    }

    if (fieldName) {
      setValidationErrors({
        key: fieldName,
        value:
          error.code === 'card_not_activated'
            ? 'card_not_activated'
            : error.message,
      });
    } else {
      setFormError(error.message);
    }
  };
  // Set Handlers
  // eslint-disable-next-line consistent-return
  async function verifyCard() {
    // Run function to check for form errors.
    // Throws a generic error message if any of the fields are not valid
    if (checkFormValidation()) return;
    // Extract expiry month and year
    const expiryMatches = cardInfo.expiry.match(/^(\d{2})\/(\d{4})$/);
    const expiryMonth = expiryMatches[1];
    const expiryYear = expiryMatches[2];

    const verifyPayload = {
      card_number: cardInfo.cardNumber,
      cvv: cardInfo.cvv,
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
    };

    try {
      setActivationMessage(null);
      setLoading(true);
      setFormError(false);

      let activationState = 'newlyactivated';

      const res = await post(
        '/api/v1/cardholder_portal/activate_card',
        verifyPayload
      );

      if (res.data.code === 'already_activated') {
        activationState = 'alreadyactivated';
      }

      // Clear All Input Fields and Error Values
      updateCardInfo({ cardNumber: '', cvv: '', expiry: '' });
      updateCardInfo({ key: 'cardNumber', value: '' });
      updateCardInfo({ key: 'cvv', value: '' });
      updateCardInfo({ key: 'expiry', value: '' });

      setValidationErrors({ key: 'cardNumber', value: '' });
      setValidationErrors({ key: 'cvv', value: '' });
      setValidationErrors({ key: 'expiry', value: '' });

      // Render Success Message
      setActivationMessage({ type: activationState });
    } catch (error) {
      handleApiError(error);
      setTermsConsent(false);
      setCardholderAgreement(false);
      setPrivacyPolicy(false);
      setElectronicDisclosure(false);
    }
    setLoading(false);
  }

  const handleActivateKeyUp = async e => {
    if (e.keyCode === 13) {
      await verifyCard();
    }
  };

  const areAllAgreementsChecked = () => {
    if (theme.brand.activateCardholderAgreementEn && theme.brand.activateCardholderAgreementFr && !cardholderAgreement) {
      return false;
    }
    if (theme.brand.activatePrivacyPolicyEn && theme.brand.activatePrivacyPolicyFr && !privacyPolicy) {
      return false;
    }
    if (theme.brand.activateElectronicComConsentEn && theme.brand.activateElectronicComConsentFr && !electronicDisclosure) {
      return false;
    }
    if ((!theme.brand.activateCardholderAgreementEn || !theme.brand.activateCardholderAgreementFr)) {
      return termsConsent;
    }

    return !termsConsent;
  };

  const submitButtonDisabled = !areAllAgreementsChecked();

  // TODO: Need to get correct wording for this page from Operations
  return (
    <AuthLayout
      page={page}
      heading={intl.messages[`${page}-heading`]}
      subHeaderOne={intl.messages[`${page}-subheader1-text`]}
      subHeaderTwo={intl.messages[`${page}-subheader2-text`]}
      subHeaderLogin={intl.messages[`${page}-subheader-login-text`]}
      subHeaderAgreement={intl.messages[`${page}-subheader-agreement-text`]}
      error={formError}
    >
      <AuthForm
        page={page}
        onSubmit={e => {
          e.preventDefault();
          verifyCard();
        }}
        onKeyUp={handleActivateKeyUp}
        loading={loading}
        submitButtonDisabled={submitButtonDisabled}
      >
        {activationMessage && (
          <ActivationMessage type={activationMessage.type} />
        )}

        <InputGroup
          label={intl.messages['auth-card-number-label']}
          labelFor="card-number"
          page="auth"
        >
          <Input
            className={`${validationErrors.cardNumber ? 'is-invalid' : ''}`}
            placeholder="•••• •••• •••• ••••"
            value={cardInfo.cardNumber}
            onBlur={() => validateCardNumber(setValidationErrors, cardInfo)}
            minLength={13}
            maxLength={19}
            onChange={e => {
              return updateCardInfo({
                key: 'cardNumber',
                value: e.target.value.trim(),
              });
            }}
          />
          {validationErrors.cardNumber && (
            <FieldError className="invalid-feedback">
              {validationErrors.cardNumber === 'card_not_activated' ? (
                <>
                  This card is not yet activated. Activate your card{' '}
                  <a href={`${window.origin}/activate`}>
                    <strong>here.</strong>
                  </a>
                </>
              ) : (
                validationErrors.cardNumber
              )}
            </FieldError>
          )}
        </InputGroup>
        <FormRow>
          <InputGroup
            label={intl.messages['auth-cvv-label']}
            labelFor="cvv"
            page="auth"
          >
            <PasswordFieldWrapper>
              <Input
                className={`${validationErrors.cvv ? 'is-invalid' : ''}`}
                placeholder="•••"
                value={cardInfo.cvv}
                onBlur={() => validateCVV(setValidationErrors, cardInfo)}
                minLength={3}
                maxLength={4}
                type={cvvType}
                onChange={e => {
                  return updateCardInfo({
                    key: 'cvv',
                    value: e.target.value.trim(),
                  });
                }}
              />
              <PwdEyeButton type={cvvType} settype={(t) => setCvvType(t)} />
            </PasswordFieldWrapper>
            {validationErrors.cvv && (
              <FieldError className="invalid-feedback">
                {validationErrors.cvv}
              </FieldError>
            )}
          </InputGroup>
          <InputGroup
            label={intl.messages['auth-expiry-label']}
            labelFor="expiry"
            page="auth"
          >
            <Input
              className={`${validationErrors.expiry ? 'is-invalid' : ''}`}
              value={cardInfo.expiry}
              onBlur={e => {
                if (e.target.value.trim()) {
                  validateExpiry(setValidationErrors, {...cardInfo, expiry: e.target.value.replace(/\//g, '')});
                } else {
                  setValidationErrors({
                    key: 'expiry',
                    value: 'Expiry is required, Format: MM/YYYY.',
                  });
                }
              }}
              placeholder="MM/YYYY"
              maxLength={7}
              minLength={7}
              onChange={(e) => {
                let { value } = e.target;
              
                // Remove all characters except digits and '/'
                value = value.replace(/[^\d/]/g, '');
              
                // Prevent more than one '/'
                const slashCount = (value.match(/\//g) || []).length;
                if (slashCount > 1) {
                  const parts = value.split('/');
                  value = `${parts[0]}/${parts[1].replace(/\//g, '')}`;
                }
              
                // Auto-insert '/' if only digits are entered and length is 3
                if (/^\d{3}$/.test(value)) {
                  value = `${value.slice(0, 2)}/${value.slice(2)}`;
                }
              
                // If length > 3 and slash is not at index 2, fix it
                if (value.length > 3 && value.indexOf('/') !== 2) {
                  const digits = value.replace(/\//g, '');
                  value = `${digits.slice(0, 2)}/${digits.slice(2, 6)}`;
                }
              
                updateCardInfo({
                  key: 'expiry',
                  value,
                });
              }}                         
            />
            {validationErrors.expiry && (
              <FieldError className="invalid-feedback">
                {validationErrors.expiry}
              </FieldError>
            )}
          </InputGroup>
        </FormRow>
        {(theme.brand.activateElectronicComConsentEn && theme.brand.activateElectronicComConsentFr)  && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-electronic-com-consent-label"
                  description="Label for Electronic Communication Consent"
                  defaultMessage={authEn['activate-electronic-com-consent-label']}
                  values={{
                    link: (
                      <ElectronicComConsentLink />
                    ),
                  }}
                />
              )}
              labelFor="activateElectronicCommConsentInput"
              id="activate-electronic-comm-consent-input"
              value={electronicDisclosure}
              onChange={e => setElectronicDisclosure(e.target.checked)}
              required
            />
          </FormRow>
        )}
        {(!theme.brand.activateCardholderAgreementEn || !theme.brand.activateCardholderAgreementFr) && (
          <FormRow>
            <Checkbox
              label={intl.messages['activate-terms-consent-label']}
              labelFor="termsConsentInput"
              id="terms-consent-input"
              value={termsConsent}
              onChange={e => setTermsConsent(e.target.checked)}
              required
            />
          </FormRow>
        )}
        {(theme.brand.activateCardholderAgreementEn && theme.brand.activateCardholderAgreementFr) && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-cardholder-agreement-label"
                  description="Label for Cardholder Agreement"
                  defaultMessage={authEn['activate-cardholder-agreement-label']}
                  values={{
                    link: (
                      <CardholderAgreementLink />
                    ),
                  }}
                />
              )}
              labelFor="activateCardholderAgreementInput"
              id="activate-cardholder-agreement-input"
              value={cardholderAgreement}
              onChange={e => setCardholderAgreement(e.target.checked)}
            />
          </FormRow>
        )}
        {(theme.brand.activatePrivacyPolicyEn && theme.brand.activatePrivacyPolicyFr) && (
          <FormRow>
            <Checkbox
              label={(
                <FormattedMessage
                  id="activate-privacy-policy-label"
                  description="Label for Privacy Policy"
                  defaultMessage={authEn['activate-privacy-policy-label']}
                  values={{
                    link: (
                      <PrivacyPolicyAgreementLink />
                    ),
                  }}
                />
              )}
              labelFor="activatePrivacyPolicyInput"
              id="activate-privacy-policy-input"
              value={privacyPolicy}
              onChange={e => setPrivacyPolicy(e.target.checked)}
              required
            />
          </FormRow>
        )}
      </AuthForm>
    </AuthLayout>
  );
};
